// Client Outbox حالت آفلاین (ARD-offline §۲ و §۳؛ AP-OFF-01): هر عملیات با client_operation_id (UUIDv7)، شعبه، occurred_at محلی و Payload رمزشده
// در گاوصندوق می‌نشیند و پس از اتصال به ترتیب در دسته‌های ۵۰تایی به /api/v1/sync/operations فرستاده می‌شود. سرور روی client_operation_id
// Idempotent است: قطع اتصال در میانه پاسخ ← ارسال دوباره همان عملیات با کلید Idempotency تازه بی‌اثر تکراری (Sync_Idempotent_On_Resend).
import type {
  BilOfflineCheckoutInput,
  BilOfflinePatientInput,
  BilSyncOperationInput,
  BilSyncOperationsRequest,
  BilSyncOperationsResponse,
} from '@phoenix/api-client';
import { uuidv7 } from './ids';
import type { OfflineVault, OutboxDisplay, OutboxEntry, OutboxStatus } from './vault';

export const SYNC_BATCH_SIZE = 50;

/** ارسال یک دسته (Endpoint همگام‌سازی با کلید Idempotency). */
export type SyncTransport = (request: BilSyncOperationsRequest, idempotencyKey: string) => Promise<BilSyncOperationsResponse>;

export interface SyncSummary {
  sent: number;
  applied: number;
  conflicts: number;
}

function iso(now: number): string {
  return new Date(now).toISOString();
}

export function checkInOperation(branchId: string, appointmentId: string, now: number = Date.now()): BilSyncOperationInput {
  return { clientOperationId: uuidv7(now), kind: 'CheckIn', occurredAt: iso(now), branchId, checkIn: { appointmentId } };
}

export function registerOperation(branchId: string, patient: BilOfflinePatientInput, now: number = Date.now()): BilSyncOperationInput {
  return { clientOperationId: uuidv7(now), kind: 'PatientRegister', occurredAt: iso(now), branchId, patient };
}

export function checkoutOperation(branchId: string, checkout: BilOfflineCheckoutInput, now: number = Date.now()): BilSyncOperationInput {
  return { clientOperationId: uuidv7(now), kind: 'Checkout', occurredAt: iso(now), branchId, checkout };
}

/** ثبت عملیات تازه در Outbox. */
export async function enqueue(vault: OfflineVault, operation: BilSyncOperationInput, display?: OutboxDisplay, now: number = Date.now()): Promise<OutboxEntry> {
  const entry: OutboxEntry = { operation, status: 'pending', createdAt: now, display };
  await vault.put(entry);
  return entry;
}

function toStatus(value: string): OutboxStatus {
  switch (value) {
    case 'Applied':
      return 'applied';
    case 'Resolved':
      return 'resolved';
    case 'Dismissed':
      return 'dismissed';
    default:
      return 'conflict';
  }
}

/**
 * همگام‌سازی عملیات در انتظار به ترتیب ایجاد. خطای شبکه ← عملیات در انتظار می‌مانند و بعداً دوباره فرستاده می‌شوند (سرور Idempotent)؛ هر نتیجه
 * (اعمال، تعارض) رمزشده در همان ردیف ثبت می‌شود.
 */
export async function syncOutbox(vault: OfflineVault, send: SyncTransport, deviceId: string, now: () => number = Date.now): Promise<SyncSummary> {
  const pending = await vault.pending();
  const summary: SyncSummary = { sent: 0, applied: 0, conflicts: 0 };
  for (let start = 0; start < pending.length; start += SYNC_BATCH_SIZE) {
    const batch = pending.slice(start, start + SYNC_BATCH_SIZE);
    const response = await send({ deviceId, operations: batch.map((entry) => entry.operation) }, uuidv7(now()));
    const byId = new Map(batch.map((entry) => [entry.operation.clientOperationId, entry]));
    for (const result of response.results) {
      const entry = byId.get(result.clientOperationId);
      if (!entry) {
        continue;
      }
      const status = toStatus(result.status);
      await vault.put({ ...entry, status, result, syncedAt: now() });
      summary.sent += 1;
      if (status === 'conflict') {
        summary.conflicts += 1;
      } else {
        summary.applied += 1;
      }
    }
  }
  return summary;
}
